import { Router } from '@angular/router';
import { AuthenticationService } from './services/authentication.service';
import { InitService } from './services/init/init.service';
import { LoadingService } from './services/init/loading-service';
import { UserService } from './services/user/user.service';
import { User } from '@nucleus/user-ui';

const BOOKMARK_KEY = 'ts_redirect';

/** Reveals the body once preferences are hydrated. index.html keeps body
 *  visibility hidden until this fires — so the app never renders with the
 *  wrong theme. Idempotent + double-rAF so the theme attribute change has
 *  actually painted before we unhide. */
function revealApp(): void {
    if (typeof document === 'undefined') return;
    requestAnimationFrame(() => requestAnimationFrame(() => {
        document.body.style.visibility = 'visible';
    }));
}

export function appInitializer(authenticationService : AuthenticationService,
    loadingService : LoadingService, initService: InitService,
    userService: UserService, router: Router) {
    return () => {
        // Capture the bookmarked route NOW — before Angular's router processes
        // the URL and redirects to /home. The OAuth round-trip strips the hash
        // (redirect_uri is host-only), so we persist it to sessionStorage and
        // recover after the BFF callback bounces us back to /. (TASK-125 C-3)
        let originalHash = window.location.hash?.replace(/^#\/?/, '') || '';

        // Pre-OAuth: stash the bookmark.
        if (originalHash && originalHash !== '/' && originalHash !== 'home') {
            sessionStorage.setItem(BOOKMARK_KEY, originalHash);
        }

        // Post-OAuth (BFF set cookies and redirected back to '/'): hash empty,
        // pull the stash to restore the user's deep link.
        if (!originalHash) {
            originalHash = sessionStorage.getItem(BOOKMARK_KEY) || '';
        }

        loadingService.loadInProgress();

        /* Returning the Promise so APP_INITIALIZER actually waits. Body is
         * hidden via index.html style; we reveal only once preferences
         * (incl. theme) are loaded — no flash of wrong palette. */
        return authenticationService.checkAuthentication()
            .then(isAuthenticated => {
                if (!isAuthenticated) {
                    // Body stays hidden, no flash before OAuth redirect.
                    authenticationService.authenticate();
                    return Promise.reject('redirecting');
                }
                // BFF: tokens are HttpOnly cookies, so the Angular layer can't
                // decode the JWT to get userId/authorities. Pull them from
                // /oauth2/me before initService.init() so guards see populated
                // authorities.
                return authenticationService.loadSessionId()
                    .then(() => authenticationService.loadProfile())
                    .then(() => loadMyProfileForTheme(userService))
                    .then(() => initService.init());
            })
            .then(() => {
                loadingService.loadEnded();
                sessionStorage.removeItem(BOOKMARK_KEY);
                if (originalHash && originalHash !== '/' && originalHash !== 'home') {
                    router.navigateByUrl('/' + originalHash);
                } else {
                    router.navigate(['/home']);
                }
                revealApp();
            })
            .catch(err => {
                // 'redirecting' is expected when not authenticated — keep body
                // hidden until the browser navigates away to OAuth login.
                if (err !== 'redirecting') {
                    loadingService.loadEnded();
                    router.navigate(['/home']);
                    revealApp();
                }
            });
    }
}

/** Calls /user/me. UserService.getMyProfile already hydrates ThemeService
 *  from user.userPreferences.theme — by the time this resolves, the
 *  data-theme attribute is final. Errors swallowed: the inline-script
 *  fallback in index.html still applied cookie/localStorage, good enough. */
function loadMyProfileForTheme(userService: UserService): Promise<void> {
    return new Promise<void>((resolve) => {
        userService.getMyProfile({
            onStart: () => {},
            onSuccess: (_user: User) => resolve(),
            onError: () => resolve(),
            onComplete: () => {},
        });
    });
}
